import { useNavigate } from "react-router-dom";
import { Tv2 } from "lucide-react";
import { useAuth } from "../context/AuthContext";

/**
 * LiveOnBetwayBadge — pill badge surfaced on live cards whose fixture is
 * confirmed available on the user's region of Betway.  Click behaviour:
 *
 * - Logged in + Betway linked  → invokes `onWatch()` to open the player
 *   modal which auto-prefers the Betway HLS extractor.
 * - Logged in + NOT linked     → navigates to /account so the user can
 *   link their Betway account (this is the primary conversion hook).
 * - Logged out                 → navigates to /auth?mode=register&next=/account.
 *
 * Props
 *   onWatch  : () => void   — caller triggers their existing stream modal
 *   compact  : boolean      — shrinks the pill for tight layouts
 */
export const LiveOnBetwayBadge = ({ onWatch, compact = false }) => {
  const navigate = useNavigate();
  const { user, token } = useAuth() || {};
  const linked = !!user?.has_betway_account;

  const handleClick = (e) => {
    e?.stopPropagation();
    if (!token) {
      navigate("/auth?mode=register&next=%2Faccount");
      return;
    }
    if (!linked) {
      navigate("/account");
      return;
    }
    onWatch && onWatch();
  };

  const label = linked ? "Watch on Betway" : token ? "Link Betway · Watch live" : "Live on Betway";

  return (
    <button
      type="button"
      onClick={handleClick}
      data-testid="live-on-betway-badge"
      className={`inline-flex items-center gap-1.5 rounded-full font-semibold uppercase tracking-wider transition-all
        bg-gradient-to-r from-emerald-500/15 to-emerald-600/10
        border border-emerald-500/40 text-emerald-300
        hover:from-emerald-500/25 hover:to-emerald-600/15 hover:border-emerald-400/60 hover:text-emerald-200
        ${compact ? "h-6 px-2 text-[9px]" : "h-7 px-2.5 text-[10px]"}`}
      title={linked
        ? "Open the live broadcast pulled from your Betway account"
        : token
          ? "Link your Betway account to unlock the live stream"
          : "Sign up + link Betway to watch this match live"}
    >
      <span className="relative flex w-1.5 h-1.5">
        <span className="absolute inline-flex w-full h-full rounded-full bg-emerald-400 opacity-75 animate-ping" />
        <span className="relative inline-flex w-1.5 h-1.5 rounded-full bg-emerald-400" />
      </span>
      <Tv2 className={compact ? "w-2.5 h-2.5" : "w-3 h-3"} />
      <span>{label}</span>
    </button>
  );
};

export default LiveOnBetwayBadge;
